/**
 * MTE — PasswordField component (M-C2.0+ Phase 4 wiring, spec Boussole v2.1 ratif Ana 18/09 ~15:10).
 *
 * Champ password réutilisable : label + input avec icône cadenas à gauche + bouton
 * de bascule de visibilité (œil ouvert / fermé) à droite. A11y :
 *  - aria-label dynamique sur le bouton (collapsed/expanded)
 *  - aria-describedby pointe vers le message d'erreur
 *  - aria-invalid pour les lecteurs d'écran
 *  - focus-visible géré par tokens.css (Layer 2 sémantique)
 *
 * Source spec : `boussole-docs/mte-entree-restructure-v2.md` §6.2 + Q3.
 * Pattern DRY entre Card 1 (Famille) et Card 2 (Enseignant) de la page /entree/.
 */
import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import styles from './PasswordField.module.css';

export interface PasswordFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Label visible au-dessus du champ. */
  label: string;
  /** Message d'erreur affiché sous le champ (validation). */
  error?: string;
  /** id du champ — utilisé pour relier label / aria-describedby. */
  id: string;
}

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  function PasswordField({ label, error, id, ...inputProps }, ref): JSX.Element {
    const [visible, setVisible] = useState(false);
    const errorId = error ? `${id}-error` : undefined;
    const describedBy = errorId ?? inputProps['aria-describedby'];

    return (
      <div className={styles.field}>
        <label htmlFor={id} className={styles.label}>
          {label}
        </label>
        <div className={`${styles.wrap} ${error ? styles.wrapError : ''}`}>
          <svg
            className={styles.iconLock}
            width={20}
            height={20}
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              d="M5 9V7a5 5 0 0 1 10 0v2h.5a1.5 1.5 0 0 1 1.5 1.5v6a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 3 16.5v-6A1.5 1.5 0 0 1 4.5 9H5Zm2 0h6V7a3 3 0 1 0-6 0v2Z"
              fill="currentColor"
            />
          </svg>
          <input
            ref={ref}
            id={id}
            type={visible ? 'text' : 'password'}
            autoComplete={inputProps.autoComplete ?? 'current-password'}
            className={styles.input}
            aria-invalid={Boolean(error) || undefined}
            aria-describedby={describedBy}
            {...inputProps}
          />
          <button
            type="button"
            className={styles.toggle}
            onClick={() => setVisible((v) => !v)}
            aria-label={
              visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'
            }
            aria-pressed={visible}
            data-testid={`${id}-toggle`}
          >
            {visible ? (
              <svg width={20} height={20} viewBox="0 0 20 20" aria-hidden="true">
                <path
                  d="M3.7 3.7a1 1 0 0 1 1.4 0l11.2 11.2a1 1 0 1 1-1.4 1.4L14 15.4A8.4 8.4 0 0 1 10 16.5C5.5 16.5 2 13 0.7 10.6a1 1 0 0 1 0-1.2 13 13 0 0 1 4.5-3.9L3.7 5.1a1 1 0 0 1 0-1.4Zm6.3 4.6a2.5 2.5 0 0 0 3.4 3.4l-3.4-3.4Zm-3-1.2L8.3 8.7a2.5 2.5 0 0 0 3 3l1.3 1.3a4 4 0 0 1-5.6-5.6ZM10 3.5c4.5 0 8 3.5 9.3 5.9a1 1 0 0 1 0 1.2 13 13 0 0 1-3 3l-1.3-1.3a11 11 0 0 0 2.4-2.1C15.8 8.3 13.2 5.5 10 5.5c-.7 0-1.3.1-1.9.3L6.7 4.4A8.4 8.4 0 0 1 10 3.5Z"
                  fill="currentColor"
                />
              </svg>
            ) : (
              <svg width={20} height={20} viewBox="0 0 20 20" aria-hidden="true">
                <path
                  d="M10 3.5C5.5 3.5 2 7 0.7 9.4a1 1 0 0 0 0 1.2C2 13 5.5 16.5 10 16.5s8-3.5 9.3-5.9a1 1 0 0 0 0-1.2C18 7 14.5 3.5 10 3.5Zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm0-6.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z"
                  fill="currentColor"
                />
              </svg>
            )}
          </button>
        </div>
        {error && (
          <span id={errorId} className={styles.error} role="alert">
            {error}
          </span>
        )}
      </div>
    );
  },
);
